//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Button, Grid, InputAdornment, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

import { EthAddr } from '@shared/SchemaTypes'

import {
  commaDecStringToBigNumberWei,
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  erc20WeiToErc20DecString,
  toCommaDecString,
  weiToNumber,
} from '@shared/conversions'
import { theme } from '@react/components/theme'

import { textFieldValueToBigNumberWei } from '@react/utils/textFieldsUtils'

import { PoolEventCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { repayEarlyWithdraw } from '@react/signedTxs/repayEarlyWithdrawTx'
import { EarlyWithdrawInfoRow } from './BorrowerAcceptEarlyRedeemRequestForm'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { fetchPoolTermEarlyWithdrawEventCTOs } from '@react/fetchers/PoolFetchers'

export default function BorrowerRepayEarlyRedeemForm(props: {
  poolAddr: EthAddr
  lenderAddr: EthAddr
  data?: any
}): ReactElement {
  const { poolAddr, lenderAddr, data } = props

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { signer },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const chainState = useOtSelector((state) => state.chainState)

  const [amount, setAmount] = React.useState<string>(erc20WeiToErc20DecString('0'))
  const [errorAmount, setErrorAmount] = React.useState<string | undefined>(undefined)
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolOverviewCTO)
  const [submitting, setSubmitting] = React.useState(false)

  const [withdrawEarlyEventCTOs, setWithdrawEarlyEventCTOs] = useState<PoolEventCTO[]>([])

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }
  const loanAddr = poolCTO?.activeLoanState.loanAddr
  useEffect(() => {
    async function fetchEarlyWithdraws(): Promise<void> {
      if (providerReadOnly && signer && loanAddr && serverApiBaseUrl && restAPIKey && loanAddr !== ETH_ADDR_ZERO) {
        const requests = await fetchPoolTermEarlyWithdrawEventCTOs(
          serverApiBaseUrl,
          restAPIKey,
          poolAddr,
          loanAddr,
          null
        )
        setWithdrawEarlyEventCTOs(requests)
      }
    }

    void fetchEarlyWithdraws()
  }, [poolCTO, loanAddr])

  const earlyWithdrawApprovedPrincipal = withdrawEarlyEventCTOs
    .filter(
      (eventCTO: PoolEventCTO) => eventCTO.eventType === 'WithdrawEarlyAccepted' && eventCTO.accountAddr === lenderAddr
    )
    .map((eventCTO: PoolEventCTO) => eventCTO.principal)
    .reduce(
      (accumulator: number, principal: string | undefined) => accumulator + (principal ? Number(principal) : 0),
      0
    )

  const errorCheck = (amountBN: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', amountBN)

    if (amountBN === 0n) {
      setErrorAmount('Amount can not be zero')
    } else {
      setErrorAmount(undefined)
    }
  }

  const handleOnFocusAmount = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnFocusPPT1', amount)
    const ppt = commaDecStringToBigNumberWei(amount)
    setAmount(commaDecStringToErcDecString(amount))

    errorCheck(ppt)
  }
  const handleOnBlurAmount = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    logOT.debug('handleOnBlur1', amount)
    setAmount(toCommaDecString(amount))
  }
  const handleOnChangeAmount = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logOT.debug('handleOnChange', event.target.value)

    setAmount(event.target.value)
    try {
      const ppt = textFieldValueToBigNumberWei(event)

      errorCheck(ppt)
    } catch (e) {
      logOT.error(e)
    }
  }
  const repayEarlyWithdrawAction = async (): Promise<void> => {
    setSubmitting(true)
    try {
      const { webSocket } = chainState
      const { chainConfigurationName } = chainState.chainConfiguration

      if (!signer) {
        throw new Error('Signer is not available')
      }

      await repayEarlyWithdraw(
        webSocket,
        signer,
        poolAddr,
        lenderAddr,
        commaDecStringToErc20Wei(amount),
        chainConfigurationName
      )

      const event = new Event('modalClose')
      window.dispatchEvent(event)
    } catch (error) {
      logOT.error('Early withdrawal repayment failed:', error)
    } finally {
      setSubmitting(false)
    }
  }
  const disableButton = weiToNumber(poolCTO?.borrowerVaultBalance) < Number(amount)

  return (
    <div data-testid="REPAY_EARLY_REDEEM_FORM">
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Please set the Early Withdrawal Amount for the Early Withdrawal Request.
          </Typography>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            This is the amount of {poolCTO.liquidityTokenSymbol} the Lender will receive for this Early Withdrawal
            Request. It should be equal to the Net Sale Proceeds of the Loan Collateral liquidated.
          </Typography>
          <Typography variant="body" sx={{ marginBottom: 4 }}>
            Total Early Withdraw accepted principal {earlyWithdrawApprovedPrincipal} in {poolCTO.liquidityTokenSymbol}{' '}
            Wei{' '}
          </Typography>
        </Grid>
      </Grid>

      {data !== undefined && <EarlyWithdrawInfoRow lenderAddr={lenderAddr} data={data} />}

      <Grid container sx={{ marginTop: 2 }}>
        <Grid item xs={12}>
          <InputLabel htmlFor="amount_liquidity">Early Withdrawal Amount</InputLabel>
          <TextField
            id="amount_liquidity"
            InputProps={{
              endAdornment: <InputAdornment position="start">{poolCTO.liquidityTokenSymbol}</InputAdornment>,
            }}
            inputProps={{
              'data-testid': 'amount_liquidity',
            }}
            sx={{ width: '300px' }}
            variant="outlined"
            placeholder="Enter Withdraw Amount"
            value={amount}
            type={'number'}
            onFocus={handleOnFocusAmount}
            onChange={handleOnChangeAmount}
            onBlur={handleOnBlurAmount}
            error={!!errorAmount}
            helperText={errorAmount}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Button
            disabled={submitting || disableButton || Number(amount) <= 0.00001}
            sx={sx}
            onClick={() => {
              repayEarlyWithdrawAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Repay the Early Withdraw Request</Typography>
          </Button>
        </Grid>
        {disableButton && (
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            The amount you are trying to send for withdraw is greater than the amount of {poolCTO.liquidityTokenSymbol}{' '}
            available in your vault ({weiToNumber(poolCTO?.borrowerVaultBalance)}). Please deposit funds to the
            borrowerVault
          </Typography>
        )}
      </Grid>
    </div>
  )
}
